/**
 * Storage volume formatting — **1024-based / binary (GiB)**.
 *
 * Object storage measures volume in binary units (1 GiB = 2^30 B), matching the
 * Wasabi console AND datacentral billing, which divides StorageOSS bytes by 1024³
 * (see servicemgmt `usage_billing.go` / datacentral `inv.usages.go` safeUsageRound).
 * CDN/traffic volume stays decimal (1000-based `formatBytes` in `_shared`) — do NOT
 * use this for anything but storage-at-rest sizes.
 *
 * 2 decimals for GiB and up, 1 for MiB/KiB, 0 for bytes — mirrors `formatBytes`.
 */
export function formatGiB(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v) || v <= 0) return '0 B';
  const k = 1024;
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(v) / Math.log(k)));
  const dp = i >= 3 ? 2 : i >= 1 ? 1 : 0;
  return `${(v / Math.pow(k, i)).toFixed(dp)} ${units[i]}`;
}

/**
 * Storage volume **pinned to GiB** — never auto-scales to MiB/TiB. Billing is
 * per GiB-month (binary 1024³), so the reconciliation surface shows every figure
 * in the same GiB unit the invoice uses, making the stats strip and per-bucket
 * rows directly comparable. Thousands-separated with 2 decimals ("2,048.31 GiB");
 * a sub-GiB bucket reads "0.48 GiB" instead of collapsing to "0 GiB".
 */
export function formatGiBExact(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v) || v <= 0) return '0 GiB';
  const gib = v / 1024 ** 3;
  return `${gib.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GiB`;
}
